import { dragDropScrolling } from '@nsr/react-drag-drop-control';
import { colorCtaBlueTint } from '@ellucian/react-design-system/core/styles/tokens';
import { CONSTANTS } from '../../Helpers/Configuration';
import { reorderAllControls } from '../../Helpers/TemplateHelpers';
import { resetHoverStyles } from './dragDropHelper';

const { addEventListenerForContainer, removeEventListenerForContainer } = dragDropScrolling;
const { appTemplateControlTypes } = CONSTANTS;
const hoverStates = {
    sameControl: 'sameControl',
    differentRowVerticalTop: 'differentRowVerticalTop',
    differentRowVerticalLast: 'differentRowVerticalLast',
    none: 'none',
};
const hoverLocations = { none: 'none', top: 'top', bottom: 'bottom' };
const transitionStyle = { transition: 'padding 0.2s 0s, background-color 0.2s 0s' };
const paddingStyleTop = {
    padding: '80px 0 0 0',
    backgroundImage: `linear-gradient(${colorCtaBlueTint} 80px, transparent 80px)`,
};
const paddingStyleBottom = {
    padding: '0 0 84px 0',
    backgroundImage: `linear-gradient(transparent calc(100% - 84px), ${colorCtaBlueTint} calc(100% - 84px))`,
};
let prevHoverState = hoverStates.sameControl;
let prevDragId = 0;
let prevHoverId = 0;

const getOnDragSectionData = (sections, dragId, hoverId) => {
    let dragSectionIndex, hoverSectionIndex;
    dragSectionIndex = sections.findIndex(s => s.id === dragId);
    hoverSectionIndex = sections.findIndex(s => s.id === hoverId);
    return { dragSectionIndex, hoverSectionIndex };
};

export const beginMoveTypeSection = (sections) => {
    return resetHoverStyles(sections);
};

export const moveTypeSection = (dragId, hoverId, dragRef, dragMonitor, domContainerId, sections, hoverStyles) => {
    let newHoverStyles = { ...hoverStyles };
    const { dragSectionIndex, hoverSectionIndex } = getOnDragSectionData(sections, dragId, hoverId);

    addEventListenerForContainer(domContainerId);
    let loc = hoverLocations.none;
    // Determine mouse position
    const clientOffset = dragMonitor.getClientOffset();
    if (!clientOffset) { return newHoverStyles; }
    // Determine rectangle on screen
    const hoverBoundingRect = dragRef.current.getBoundingClientRect();
    // Get vertical center
    const hoverY1 =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    if (hoverClientY < hoverY1) { loc = hoverLocations.top; }
    else { loc = hoverLocations.bottom; }

    let hoverState;
    // Hover on same control
    if (dragId === hoverId) {
        hoverState = hoverStates.sameControl;
    }
    // Hover on the top part of another section
    else if (hoverSectionIndex - dragSectionIndex !== 1 &&
        loc === hoverLocations.top) {
        hoverState = hoverStates.differentRowVerticalTop;
    }
    // Hover on the bottom part of last section
    else if (hoverSectionIndex === sections.length - 1 &&
        loc === hoverLocations.bottom) {
        hoverState = hoverStates.differentRowVerticalLast;
    }
    else { hoverState = hoverStates.none; }

    // Same as previous relevant hover area
    if (hoverState === prevHoverState && hoverId === prevHoverId && dragId === prevDragId) {
        return newHoverStyles;
    }

    prevHoverState = hoverState;
    prevHoverId = hoverId;
    prevDragId = dragId;
    newHoverStyles = resetHoverStyles(sections);
    newHoverStyles[dragId] = { opacity: '0.5' };

    // Set the hoverStyles based on the hover state
    switch (hoverState) {
        case hoverStates.sameControl:
            break;
        case hoverStates.differentRowVerticalTop:
            newHoverStyles[hoverId] = { ...transitionStyle, ...paddingStyleTop };
            break;
        case hoverStates.differentRowVerticalLast:
            newHoverStyles[hoverId] = { ...transitionStyle, ...paddingStyleBottom };
            break;
        default:
    }
    return newHoverStyles;
};

export const dropTypeSection = (dragId, hoverId, controlGrid, selectedControl) => {
    const [pages, sections, groups, questions] = controlGrid;
    let movedSections = [...sections];
    const { dragSectionIndex, hoverSectionIndex } = getOnDragSectionData(sections, dragId, hoverId);

    const returnState = {
        dndHoverStyles: resetHoverStyles(movedSections),
        page: { newSections: movedSections, newGroups: groups, newQuestions: questions },
        selectedControl,
    };
    removeEventListenerForContainer();

    switch (prevHoverState) {
        // Remove from drag and insert above hover position
        case hoverStates.differentRowVerticalTop: {
            const insertOffset = hoverSectionIndex > dragSectionIndex ? -1 : 0;
            movedSections.splice(hoverSectionIndex + insertOffset, 0, movedSections.splice(dragSectionIndex, 1)[0]);
            break;
        }
        // Remove from drag and insert below hover position
        case hoverStates.differentRowVerticalLast:
            movedSections.splice(hoverSectionIndex, 0, movedSections.splice(dragSectionIndex, 1)[0]);
            break;
        default:
            return returnState;
    }
    const { newSections, newGroups, newQuestions } = reorderAllControls(pages, movedSections, groups, questions);
    returnState.dndHoverStyles = resetHoverStyles(movedSections);
    returnState.page = { newSections, newGroups, newQuestions };
    returnState.selectedControl = {
        controlType: appTemplateControlTypes.section,
        pageId: newSections.find(s => s.id === dragId).pageId,
        sectionId: dragId,
        groupId: null,
        questionId: null,
    };
    return returnState;
};

export const undropTypeSection = (sections) => {
    removeEventListenerForContainer();
    return resetHoverStyles(sections);
};
